import React, { createContext, useContext, useEffect, useState } from 'react';
import { Client, NavigationMenuGroup, NavigationMenuItem } from '../../core/DashboardApi/api';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import PersonIcon from '@mui/icons-material/Person';
import FolderSharedIcon from '@mui/icons-material/FolderShared';
import LocalPharmacyIcon from '@mui/icons-material/LocalPharmacy';
import EventIcon from '@mui/icons-material/Event';
import FolderCopyIcon from '@mui/icons-material/FolderCopy';
import ListAltIcon from '@mui/icons-material/ListAlt';
import BiotechIcon from '@mui/icons-material/Biotech';
import MonitorHeartIcon from '@mui/icons-material/MonitorHeart';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import PlaylistAddCircleIcon from '@mui/icons-material/PlaylistAddCircle';
import PreviewIcon from '@mui/icons-material/Preview';
import FindInPageIcon from '@mui/icons-material/FindInPage';
import PregnantWomanIcon from '@mui/icons-material/PregnantWoman';
import ReceiptIcon from '@mui/icons-material/Receipt';
import MasksIcon from '@mui/icons-material/Masks';
import MedicalServicesIcon from '@mui/icons-material/MedicalServices';
import HomeIcon from '@mui/icons-material/Home';
import FeedbackIcon from '@mui/icons-material/Feedback';
import MedicationIcon from '@mui/icons-material/Medication';
import ChildFriendlyIcon from '@mui/icons-material/ChildFriendly';
import VaccinesIcon from '@mui/icons-material/Vaccines';
import InventoryIcon from '@mui/icons-material/Inventory';
import MoveDownIcon from '@mui/icons-material/MoveDown';
import PostAddIcon from '@mui/icons-material/PostAdd';
import CompareArrowsIcon from '@mui/icons-material/CompareArrows';
import FolderDeleteIcon from '@mui/icons-material/FolderDelete';
import DescriptionIcon from '@mui/icons-material/Description';
import AssignmentIcon from '@mui/icons-material/Assignment';
import AssignmentIndIcon from '@mui/icons-material/AssignmentInd';
import CoronavirusIcon from '@mui/icons-material/Coronavirus';
import FeedIcon from '@mui/icons-material/Feed';
import FamilyRestroomIcon from '@mui/icons-material/FamilyRestroom';
import GradingIcon from '@mui/icons-material/Grading';
import RequestQuoteIcon from '@mui/icons-material/RequestQuote';
import NoteAddIcon from '@mui/icons-material/NoteAdd';
import FactCheckIcon from '@mui/icons-material/FactCheck';
import PendingActionsIcon from '@mui/icons-material/PendingActions';
import AddAlertIcon from '@mui/icons-material/AddAlert';
import MedicalInformationIcon from '@mui/icons-material/MedicalInformation';
import PlaylistAddCheckIcon from '@mui/icons-material/PlaylistAddCheck';
import CurrencyExchangeIcon from '@mui/icons-material/CurrencyExchange';
import HandshakeIcon from '@mui/icons-material/Handshake';
import AddchartIcon from '@mui/icons-material/Addchart';
import ThumbUpIcon from '@mui/icons-material/ThumbUp';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import ReportProblemIcon from '@mui/icons-material/ReportProblem';
import SettingsSuggestIcon from '@mui/icons-material/SettingsSuggest';
import QueryStatsIcon from '@mui/icons-material/QueryStats';
import PersonSearchIcon from '@mui/icons-material/PersonSearch';
import ManageSearchIcon from '@mui/icons-material/ManageSearch';
import { REACT_APP_PORTAL_API_URL } from '../../KeyVariables';
import { useNavigate } from 'react-router-dom';

interface NavigationContextType {
  Navigation: NavigationMenuGroup[];
  RecentlyNavigation: NavigationMenuItem[];
  iconMap: any;
  processNavigation: (navgiationItem: NavigationMenuItem) => void;
}

const NavigationContext = createContext<NavigationContextType>({
  Navigation: [],
  RecentlyNavigation: [],
  iconMap: null,
  processNavigation: () => { },
});

export const NavigationProvider = ({ children }) => {
  const [Navigation, setNavigation] = useState<NavigationMenuGroup[]>([]);
  const [RecentlyNavigation, setRecentlyNavigation] = useState<NavigationMenuItem[]>([]);
  const navigation = useNavigate();
  
  // ReactIcon comes from the DB, which can name an icon this UI's build does not know yet:
  // fall back to None instead of rendering undefined (that crashes the menu and the dashboard)
  const iconMap = new Proxy<Record<string, React.ElementType>>({
    None: FindInPageIcon,
    GradingIcon: GradingIcon,
    NoteAddIcon: NoteAddIcon,
    RequestQuoteIcon: RequestQuoteIcon,
    FactCheckIcon: FactCheckIcon,
    PendingActionsIcon : PendingActionsIcon ,
    AdminPanelSettingsIcon: AdminPanelSettingsIcon,
    AddAlertIcon: AddAlertIcon,
    MedicalInformationIcon: MedicalInformationIcon,
    PlaylistAddCheckIcon: PlaylistAddCheckIcon,
    CurrencyExchangeIcon: CurrencyExchangeIcon,
    HandshakeIcon: HandshakeIcon,
    AddchartIcon: AddchartIcon,
    PersonIcon: PersonIcon,
    EventIcon: EventIcon,
    LocalPharmacyIcon: LocalPharmacyIcon,
    BiotechIcon: BiotechIcon,
    FolderSharedIcon: FolderSharedIcon,
    VerifiedUserIcon: VerifiedUserIcon,
    PreviewIcon: PreviewIcon,
    ListAltIcon: ListAltIcon,
    MonitorHeartIcon: MonitorHeartIcon,
    FolderCopyIcon: FolderCopyIcon,
    PersonAddIcon:PersonAddIcon,
    PlaylistAddCircleIcon:PlaylistAddCircleIcon,
    VaccinesIcon: VaccinesIcon,
    PregnantWomanIcon: PregnantWomanIcon,
    ReceiptIcon: ReceiptIcon,
    MasksIcon: MasksIcon,
    MedicalServicesIcon: MedicalServicesIcon,
    HomeIcon: HomeIcon,
    FeedbackIcon: FeedbackIcon,
    MedicationIcon: MedicationIcon,
    ChildFriendlyIcon: ChildFriendlyIcon,
    InventoryIcon: InventoryIcon,
    MoveDownIcon: MoveDownIcon,
    PostAddIcon: PostAddIcon,
    CompareArrowsIcon: CompareArrowsIcon,
    FolderDeleteIcon: FolderDeleteIcon,
    DescriptionIcon: DescriptionIcon,
    AssignmentIcon: AssignmentIcon, 
    AssignmentInd:AssignmentIcon,
    Coronavirus:CoronavirusIcon,
    ShoppingCartIcon:ShoppingCartIcon,
    Feed:FeedIcon,
    FamilyRestroom:FamilyRestroomIcon,
    ThumbUpIcon: ThumbUpIcon,
    ReportProblemIcon:ReportProblemIcon,
    SettingsSuggestIcon:SettingsSuggestIcon,
    QueryStatsIcon:QueryStatsIcon,
    PersonSearchIcon: PersonSearchIcon,
    ManageSearchIcon: ManageSearchIcon
  }, {
    get: (icons, name) => icons[name as string] ?? icons.None
  })


  useEffect(() => {
    const recentlyNavigationFromStorage = localStorage.getItem('recentlyNavigation');
    if (recentlyNavigationFromStorage) {
      setRecentlyNavigation(JSON.parse(recentlyNavigationFromStorage));
    }

    const apiUrl = REACT_APP_PORTAL_API_URL;
    const client = new Client(apiUrl);
    setNavigation([]);
  }, [])

  const processNavigation = (menuItem: NavigationMenuItem) => {
    const isExistIndex = RecentlyNavigation.findIndex(item => item.name === menuItem.name);
    
    if (isExistIndex !== -1) {
      RecentlyNavigation.splice(isExistIndex, 1);
    }

    const updatedRecentlyNavigation = [menuItem, ...RecentlyNavigation];
    const limitedRecentlyNavigation = updatedRecentlyNavigation.slice(0, 6);
    setRecentlyNavigation(limitedRecentlyNavigation);
    localStorage.setItem('recentlyNavigation', JSON.stringify(limitedRecentlyNavigation));
    if(menuItem.navigationItem?.fullUrl?.startsWith(window.location.origin)) {
      navigation(menuItem.navigationItem?.fullUrl.split(window.location.origin)[1])
    } else {
      const token = localStorage.getItem("accessToken");
      if(menuItem.navigationItem?.fullUrl != undefined || menuItem.navigationItem?.fullUrl != null){
        let url = new URL(menuItem.navigationItem?.fullUrl);
        if(token){
          url.searchParams.append('t', token);
          url.searchParams.append('redirectUrl', window.location.href);
          window.open(url, '_self');
        }
        else{
          url.searchParams.append('redirectUrl', window.location.href);
          window.open(url, '_self');
        }
      }
    
    }

  };


  return (
    <NavigationContext.Provider value={{ Navigation, RecentlyNavigation, iconMap, processNavigation }}>
      {children}
    </NavigationContext.Provider>
  );
};

export const useIconNavigation = () => useContext(NavigationContext);
